import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue';
import { computed, ComputedRef, h, ref, unref, watch } from 'vue';
import { isBoolean } from '@mts-fe/utils';
import { t } from '@mts/hooks/web/useI18n';
import { PADMode } from '@mts/utils/env';
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../const';
import type { BasicTableProps, PaginationProps } from '../types/table';

export function usePagination(refProps: ComputedRef<BasicTableProps>) {
  const configRef = ref<PaginationProps>({});
  const show = ref(true);

  watch(
    () => unref(refProps).pagination,
    (pagination) => {
      if (!isBoolean(pagination) && pagination) {
        configRef.value = {
          ...unref(configRef),
          ...(pagination ?? {}),
        };
      }
    },
  );

  const getPaginationInfo = computed((): PaginationProps | false => {
    const { pagination } = unref(refProps);

    if (!unref(show) || (isBoolean(pagination) && !pagination)) {
      return false;
    }

    return {
      current: 1,
      size: 'small',
      defaultPageSize: PAGE_SIZE,
      showTotal: (total) => t('component.table.total', { total }),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      itemRender: ({ page, type, originalElement }) => {
        if (type === 'prev') {
          return page === 0 ? null : h(LeftOutlined);
        } else if (type === 'next') {
          return page === 1 ? null : h(RightOutlined);
        }
        return originalElement;
      },
      simple: PADMode,
      showQuickJumper: true,
      position: ['bottomLeft'],
      ...(isBoolean(pagination) ? {} : pagination),
      ...unref(configRef),
    };
  });

  function setPagination(info: Partial<PaginationProps>) {
    const paginationInfo = unref(getPaginationInfo);
    configRef.value = {
      ...(!isBoolean(paginationInfo) ? paginationInfo : {}),
      ...info,
    };
  }

  function getPagination() {
    return unref(getPaginationInfo);
  }

  function getShowPagination() {
    return unref(show);
  }

  async function setShowPagination(flag: boolean) {
    show.value = flag;
  }

  return { getPagination, getPaginationInfo, setShowPagination, getShowPagination, setPagination };
}
